/**
 * Location Edit Page
 * Edit an existing location
 */

import React from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import Card from '../components/ui/Card';
import LoadingSpinner from '../components/common/LoadingSpinner';
import LocationForm from '../components/organizations/LocationForm';
import { getLocation } from '../services/locations.service';
import { getOrganization } from '../services/organizations.service';

const LocationEdit: React.FC = () => {
  const { id, locationId } = useParams<{ id: string; locationId: string }>();
  const navigate = useNavigate();

  const { data: organization, isLoading: orgLoading } = useQuery({
    queryKey: ['organization', id],
    queryFn: () => getOrganization(id!),
    enabled: !!id,
  });

  const { data: location, isLoading, error } = useQuery({
    queryKey: ['location', locationId],
    queryFn: () => getLocation(locationId!),
    enabled: !!locationId,
  });

  const handleSuccess = () => {
    navigate(`/organizations/${id}`);
  };

  const handleCancel = () => {
    navigate(`/organizations/${id}`);
  };

  // Wait for BOTH the location AND its parent organization before rendering — otherwise the
  // archived-parent / route-parent checks below could be skipped on the first paint.
  if (isLoading || orgLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" text="Loading location..." />
      </div>
    );
  }

  if (error || !location) {
    return (
      <div className="p-6">
        <Card variant="outlined" padding="md" className="border-red-200 bg-red-50">
          <h3 className="text-red-800 font-semibold">Error loading location</h3>
          <p className="text-red-600 text-sm mt-1">
            {error instanceof Error ? error.message : 'Location not found'}
          </p>
        </Card>
      </div>
    );
  }

  // Route-parent consistency: the location MUST belong to the organization in the URL. A mismatched
  // pair (e.g. an active org id supplied with a location that belongs to an archived org) must not
  // render an editable form — redirect to the location's ACTUAL parent (whose detail page reflects its
  // real archived state). Guards the archived check below, which reads the URL org.
  if (location.organization_id && location.organization_id !== id) {
    return <Navigate to={`/organizations/${location.organization_id}`} replace />;
  }

  // Locations of a disabled (archived) organization are not editable — redirect to the org detail page.
  if (organization?.is_archived) {
    return <Navigate to={`/organizations/${id}`} replace />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate(`/organizations/${id}`)}
          className="text-gray-600 hover:text-gray-900 transition-colors"
          aria-label="Back to organization"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Edit Location</h1>
          {organization && (
            <p className="text-gray-600 mt-1">{organization.name} - {location.name}</p>
          )}
        </div>
      </div>

      {/* Form Card */}
      <Card>
        <LocationForm
          organizationId={id!}
          location={location}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      </Card>
    </div>
  );
};

export default LocationEdit;
